import { AlertTriangle, AlertCircle, Info, CheckCircle2 } from "lucide-react";

const SEV_STYLES = {
  error: {
    bg: "bg-red-950/40 border-red-500/50",
    iconColor: "text-red-400",
    icon: AlertTriangle,
    label: "Wichtig prüfen",
    labelBg: "bg-red-500/20 text-red-300",
  },
  warning: {
    bg: "bg-amber-950/40 border-amber-500/50",
    iconColor: "text-amber-400",
    icon: AlertCircle,
    label: "Hinweis",
    labelBg: "bg-amber-500/20 text-amber-300",
  },
  info: {
    bg: "bg-blue-950/40 border-blue-500/50",
    iconColor: "text-blue-400",
    icon: Info,
    label: "Info",
    labelBg: "bg-blue-500/20 text-blue-300",
  },
  ok: {
    bg: "bg-[#00E575]/10 border-[#00E575]/40",
    iconColor: "text-[#00E575]",
    icon: CheckCircle2,
    label: "Plausibel",
    labelBg: "bg-[#00E575]/20 text-[#00E575]",
  },
};

const FIELD_LABEL = {
  annual_consumption_kwh: "Jahresverbrauch",
  annual_consumption_gas_m3: "Gasverbrauch (m³)",
  heated_area: "Wohnfläche",
  warm_water_persons: "Personen Haushalt",
  calculated_heat_load_kw: "Heizlast",
};

/**
 * Plausibilitäts-Banner für Verbrauchsdaten.
 * - error: rot (Datenfehler vermutet, z. B. fehlende Einheit)
 * - warning: amber (ungewöhnlich, prüfen)
 * - info: blau (nur Hinweis)
 * - ok: grün (alles plausibel)
 */
export const PlausibilityBanner = ({ plausibility }) => {
  if (!plausibility) return null;
  const sev = plausibility.severity || "ok";
  const issues = plausibility.issues || [];

  if (sev === "ok" && issues.length === 0) {
    const s = SEV_STYLES.ok;
    const Icon = s.icon;
    return (
      <div
        data-testid="plausibility-ok"
        className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${s.bg}`}
      >
        <Icon className={s.iconColor} size={20} />
        <div>
          <div className="font-semibold text-[14px] text-white">Plausibilitätsprüfung bestanden</div>
          <div className="text-[12px] text-zinc-300 font-medium">Verbrauchsdaten liegen im erwarteten Bereich.</div>
        </div>
      </div>
    );
  }

  // Highest severity
  const order = { error: 3, warning: 2, info: 1, ok: 0 };
  const topSev = issues.reduce((acc, i) => (order[i.severity] > order[acc] ? i.severity : acc), "info");
  const s = SEV_STYLES[topSev] || SEV_STYLES.warning;
  const Icon = s.icon;

  return (
    <div
      data-testid={`plausibility-${topSev}`}
      className={`rounded-2xl border-2 p-4 ${s.bg}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`h-10 w-10 rounded-xl ${s.labelBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={s.iconColor} size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <div className="font-display font-bold text-[15px] text-white">Plausibilitätsprüfung</div>
            <span className={`px-2 py-0.5 rounded-md text-[12px] font-bold uppercase tracking-wider ${s.labelBg}`}>{s.label}</span>
          </div>
          <div className="text-[12px] text-zinc-300 font-medium">
            {issues.length} Hinweis{issues.length === 1 ? "" : "e"} zur Eingabe
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {issues.map((it, idx) => {
          const itStyle = SEV_STYLES[it.severity] || SEV_STYLES.warning;
          const ItIcon = itStyle.icon;
          return (
            <div
              key={idx}
              data-testid={`plausibility-issue-${idx}`}
              className="bg-[#0A0A0C]/60 border border-zinc-800 rounded-xl p-3 flex gap-2.5"
            >
              <ItIcon size={16} className={`${itStyle.iconColor} mt-0.5 flex-shrink-0`} />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-mono text-zinc-300 uppercase tracking-wider mb-0.5">
                  {FIELD_LABEL[it.field] || it.field}
                </div>
                <div className="text-[14px] text-white font-medium leading-snug">{it.message}</div>
                <div className="text-[13px] text-zinc-300 mt-1 leading-snug">
                  <span className="text-[#00E575]">→</span> {it.suggestion}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
